import { useEffect, useRef, useState, memo } from 'react';
import { Link } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    Button,
    CardActions,
    Chip,
    ClickAwayListener,
    Divider,
    Grid,
    Paper,
    Popper,
    Stack,
    Typography,
    Badge,
    useMediaQuery
} from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import NotificationList from './NotificationList';

// assets
import { IconBell } from '@tabler/icons';
import Pusher from "pusher-js"
import { END_POINT, ROUTE_NAME } from 'configs';
import useAuth from 'hooks/useAuth';
import { setOpen, setRead, fetchNotifications } from 'store/slices/notification';
import { dispatch, useSelector } from 'store';
import { useTranslation } from 'react-i18next';
// ==============================|| NOTIFICATION ||============================== //
const isSupported:boolean = 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
const NotificationSection = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { user, isLoggedIn } = useAuth();
    const matchesXs = useMediaQuery(theme.breakpoints.down('md'));

    const [page, setPage] = useState<number>(1);
    // const [open, setOpen] = useState(false);
    const { open, total, totalUnread, notifications } = useSelector((state) => state.notification);
    /**
     * anchorRef is used on different componets and specifying one type leads to other components throwing an error
     * */
    const anchorRef = useRef<any>(null);

    const handleToggle = () => {
        dispatch(setOpen(!open));
    };
    const readAll = () => {
        dispatch(setRead("all"));
    };

    const handleClose = (event: React.MouseEvent<HTMLDivElement> | MouseEvent | TouchEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        dispatch(setOpen(false))
    };

    const prevOpen = useRef(open);
    // handle notifications
    //  ! must https just get noti
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }
        prevOpen.current = open;
    }, [open]);

    useEffect(() => {
        if (!isSupported) return;
        if (isLoggedIn && Notification['permission'] !== 'granted' && Notification['permission'] !== 'denied') {
            Notification.requestPermission().then((per) => {
                console.log(per);
            });
        }
    }, [isLoggedIn]);

    useEffect(() => {
        if (!isSupported) return;
        const notifyMe = (id: string, title: string, des: string) => {
            if (Notification.permission === "granted") {
                const notification = new Notification(title, {
                    icon: `${END_POINT.BASE_URL}/logo.ico`,
                    body: des,
                });

                notification.onclick = function () {
                    window.open(`${END_POINT.BASE_URL}${ROUTE_NAME.complaint.view}/${id}`);
                };
            }
        }

        Pusher.logToConsole = false;
        const pusher = new Pusher(END_POINT.PUSHER_ID as string, {
            cluster: 'mt1'
        });

        const channel = pusher.subscribe('ticket-created');
        channel.bind('new-ticket', function (data: any) {
            if (data?.ticket) {
                dispatch(fetchNotifications(page));
                notifyMe(data.ticket.id, t('New ticket just created'), data.ticket.title);
            }
        });
        const channel2 = pusher.subscribe('ticket-chanel-updated');
        channel2.bind('ticket-event-updated', function (data: any) {
            if (data?.ticket && user?.id !== data.updated_by) {
                notifyMe(data.ticket.id, t('A ticket just updated'), data.ticket.title);
                dispatch(fetchNotifications(page));
            }
        });
    }, []);
    useEffect(() => {
        dispatch(fetchNotifications(page));
    }, [page]);

    return (
        <>
            <Box
                sx={{
                    ml: 2,
                    mr: 3,
                    [theme.breakpoints.down('md')]: {
                        mr: 2
                    }
                }}
            >
                <Badge
                    badgeContent={totalUnread}
                    // variant="rounded"
                    sx={{
                        ...theme.typography.commonAvatar,
                        ...theme.typography.mediumAvatar,
                        transition: 'all .2s ease-in-out',
                        background: theme.palette.background.paper,
                        color: theme.palette.grey[900],
                        '&[aria-controls="menu-list-grow"],&:hover': {
                            background: theme.palette.secondary.light,
                            color: theme.palette.grey[900]
                        },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        "& .MuiBadge-badge": {
                            height: '15px',
                            width: '15px',
                            minWidth: '15px',
                            top: '10px',
                            right: '10px',
                            fontSize: '.65rem'
                        }
                    }}
                    ref={anchorRef}
                    aria-controls={open ? 'menu-list-grow' : undefined}
                    aria-haspopup="true"
                    onClick={handleToggle}
                    color="error"
                >
                    <IconBell stroke={1.5} size="20px" />
                </Badge>
            </Box>

            <Popper
                placement={matchesXs ? 'bottom' : 'bottom-end'}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [matchesXs ? 5 : 0, 20]
                            }
                        }
                    ]
                }}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Transitions position={matchesXs ? 'top' : 'top-right'} in={open} {...TransitionProps}>
                            <Paper>
                                {open && (
                                    <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                        <Grid container direction="column" spacing={2}>
                                            <Grid item xs={12}>
                                                <Grid container alignItems="center" justifyContent="space-between" sx={{ pt: 2, px: 2 }}>
                                                    <Grid item>
                                                        <Stack direction="row" spacing={2}>
                                                            <Typography variant="subtitle1">{t("Notification")}</Typography>
                                                            {/* <Chip
                                                                size="small"
                                                                label={total}
                                                                sx={{
                                                                    color: theme.palette.background.default,
                                                                    bgcolor: theme.palette.warning.dark
                                                                }}
                                                            /> */}
                                                        </Stack>
                                                    </Grid>
                                                    {totalUnread > 0 && (
                                                        <Grid item>
                                                            <Typography component={Link} to="#" variant="subtitle2" color="primary" onClick={readAll}>
                                                                {t("Mark as all read")}
                                                            </Typography>
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <PerfectScrollbar
                                                    style={{ height: '100%', maxHeight: 'calc(100vh - 205px)', overflowX: 'hidden' }}
                                                >
                                                    <NotificationList />
                                                </PerfectScrollbar>
                                            </Grid>
                                        </Grid>
                                        <Divider />
                                        {notifications.length < total && (
                                            <CardActions sx={{ p: 1.25, justifyContent: 'center' }}>
                                                <Button size="small" disableElevation onClick={() => setPage((prev: number) => prev + 1)}>
                                                    {t("Read more")}
                                                </Button>
                                            </CardActions>
                                        )}
                                    </MainCard>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};

export default memo(NotificationSection);
